// React & Hooks
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Tabulator } from 'react-tabulator/lib/types/TabulatorTypes';
import arrowLeft from '@/assets/icon/Arrow1.png';
import arrowRight from '@/assets/icon/Arrow2.png';
import { useGroupManagement, useUsers } from '@/domains/system-management/hooks/useGroupManagement';
// Types
import type {
  ButtonState,
  Group,
  GroupParams,
  User,
  UserParams,
} from '@/domains/system-management/types/group';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import Button from '@/shared/components/ui/Button';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
// UI Components
import {
  type ColumnDefinition,
  ReactTabulator,
} from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import LogList from '@/shared/components/ui/LogList/LogList';
import Select from '@/shared/components/ui/Select/Select';
// Custom Hooks
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { formatFullDateTimeDDMMYYYY } from '@/shared/utils/date';

const GroupManagement = () => {
  const { t } = useTranslation();
  const totalText = t('cmn > total');
  const resultText = t('cmn > results');

  // tabulator top
  const commonColumnSettings = {
    hozAlign: 'center',
    headerHozAlign: 'center',
    resizable: false,
  };

  const columnsHistory = [
    {
      title: t('cmn > no'),
      formatter: (cell: Tabulator.CellComponent) => {
        const row = cell.getRow();
        const page = row.getTable().getPage();
        const pageSize = row.getTable().getPageSize();
        return (Number(page) - 1) * Number(pageSize) + row.getPosition(true);
      },
      width: 65,
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: 'Group Group',
      field: 'group_code',
      widthGrow: 1,
      headerSort: false,
      visible: false,
      ...commonColumnSettings,
    },
    {
      title: t('user > group name'),
      field: 'group_name',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: t('002'),
      field: 'usage',
      widthGrow: 2,
      headerSort: false,
      ...commonColumnSettings,
    },
  ];

  const columnStandby = [
    {
      formatter: 'rowSelection',
      titleFormatter: 'rowSelection',
      width: '60',
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: t('profile > id'),
      field: 'account_id',
      width: '120',
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: t('user > name'),
      field: 'name',
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: 'ID',
      field: 'id',
      width: '100',
      visible: false,
      headerSort: false,
      ...commonColumnSettings,
    },
  ];

  const columnAssign = [
    {
      formatter: 'rowSelection',
      titleFormatter: 'rowSelection',
      width: '60',
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: t('profile > id'),
      field: 'account_id',
      width: '120',
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: t('user > name'),
      field: 'name',
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: 'ID',
      field: 'id',
      width: '100',
      visible: false,
      headerSort: false,
      ...commonColumnSettings,
    },
  ];

  const tbRefInit = useRef<Tabulator | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const groupRef = useRef<HTMLInputElement | null>(null);
  const tbRefStandby = useRef<Tabulator | null>(null);
  const tbRefAssign = useRef<Tabulator | null>(null);

  const [disabled, setDisabled] = useState<boolean>(true);
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [disabledLog, setDisabledLog] = useState<boolean>(true);
  const [selectedIsCodeGroup, setSelectedIsCodeGroup] = useState<string>('');

  const [users, setUsers] = useState<User[]>([]);
  const [usersAssign, setUsersAssign] = useState<User[]>([]);
  const [initialUsersAssign, setInitialUsersAssign] = useState<User[]>([]);

  const [selectedPosition, setSelectedPosition] = useState<string>('');
  const [selectedAccountType, setSelectedAccountType] = useState<'All' | 'internal' | 'healthcare'>(
    'All'
  );
  const [selectedRowAssign, setSelectedRowAssign] = useState<User[] | null>(null);
  const [selectedRowStandby, setSelectedRowStandby] = useState<User[] | null>(null);
  const [groupName, setGroupName] = useState<string>('');

  const [selectedCode, setSelectedCode] = useState<{
    group_code: number | null;
    id?: string;
  }>({
    group_code: null,
  });

  //Change state
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const [isRowClicked, setIsRowClicked] = useState<boolean>(false);
  const [newId, setNewId] = useState<number | null>(null);

  const hasChangedRef = useRef(hasChanged);
  const newClickedRef = useRef(isNewClicked);
  const rowClickedRef = useRef(isRowClicked);

  useEffect(() => {
    hasChangedRef.current = hasChanged;
    newClickedRef.current = isNewClicked;
    rowClickedRef.current = isRowClicked;
  }, [hasChanged, isNewClicked, isRowClicked]);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (isNewClicked && groupRef.current) {
      groupRef.current.focus();
    }
  }, [isNewClicked]);

  //Button
  const [buttonState, setButtonState] = useState<ButtonState>({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
    restore: true,
    arrow: true,
  });

  const disableAllButtons = () => {
    setButtonState({
      confirm: true,
      cancel: true,
      delete: true,
      create: true,
      restore: true,
      arrow: true,
    });
  };

  const enableInitialButtons = () => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prevState) => ({
      ...prevState,
      create: false,
    }));
  };

  const enableUPDATEButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
      delete: false,
      create: false,
      arrow: false,
      restore: false,
    }));
  };

  const enableRegisterButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
      arrow: false,
      restore: false,
    }));
  };

  const reloadCallback = () => {
    enableInitialButtons();
    tbRefInit.current?.deselectRow();
    emptyDetail();
    setDisabled(true);
    setDisabledLog(true);
    setHasChangesUpdate(false);
    setSelectedCode({ group_code: null });
    setNewId(null);
    setIsRowClicked(false);
    setIsNewClicked(false);
    setHasChangesCreate(false);
    setHasChangesUpdate(false);
    setHasChanged(false);
    setUsers(usersData);
    setSelectedRowAssign([]);
    setSelectedRowStandby([]);
    setGroupName('');
  };

  const createCallback = () => {
    enableInitialButtons();
    emptyDetail();
    setDisabled(true);
    setHasChangesCreate(false);
    setHasChanged(false);
    setIsNewClicked(false);
    setSelectedCode({ group_code: null });
    setIsRowClicked(false);
  };
  const updateCallback = () => {
    enableUPDATEButtons();
    setHasChangesUpdate(false);
    setHasChanged(false);
    setSelectedRowAssign([]);
    setSelectedRowStandby([]);
    setSelectedCode({ group_code: null });
  };

  const [optionParams] = useState<string>('upper-code=002&upper-code=021');
  const [queryParamsUser, setQueryParamsUser] = useState<UserParams>({});

  const [queryParams, setQueryParams] = useState<GroupParams>({});

  const { useGroups, useGroup, useCreateGroup, useUserGroup, useDeleteGroup, useUpdateUserGroup } =
    useGroupManagement(queryParams);

  // Hanya akun WEB yang bisa masuk grup. Akun MOBILE (ID akun murni angka =
  // health_care_facility_id, aplikasi kru ambulans) tidak memakai otoritas
  // grup web, jadi tidak ditawarkan di sini - aturan yang sama dengan User
  // Management saat User Type = Web. Dimemo supaya tabel Tabulator tidak dibangun ulang
  // di setiap render.
  const { data: allUsersData = [] } = useUsers(queryParamsUser);
  const usersData = useMemo(
    () => allUsersData.filter((user) => !/^[0-9]+$/.test(String(user.account_id ?? ''))),
    [allUsersData]
  );
  const displayedUsers = useMemo(() => {
    if (selectedAccountType === 'All') return users;
    return users.filter((user) => user.account_type === selectedAccountType);
  }, [selectedAccountType, users]);
  const { data: groupsData = [] } = useGroups();
  const { data: groupData } = useGroup(selectedCode?.group_code);
  const { data: userGroupData } = useUserGroup(selectedCode?.group_code);
  const createGroupMutation = useCreateGroup((responseData?: { group_code: number }) => {
    createCallback();
    const newUserId = responseData?.group_code ?? null;
    setNewId(newUserId);
  });
  const deleteUserMutation = useDeleteGroup(() => {
    reloadCallback();
  });
  const updateUserGroupMutation = useUpdateUserGroup((responseData?: number) => {
    updateCallback();
    setSelectedCode({ group_code: responseData ?? null });
    setIsRowClicked(true);
  });
  const { commonListData } = useCommonCodes({ optionParams });

  const [formValues, setFormValues] = useState<Group>({
    group_name: '',
    usage: '',
  });

  const emptyDetail = () => {
    setFormValues({
      group_name: '',
      usage: '',
      description: '',
    });
  };

  const languageTabulator = () => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'),
        first_title: t('cmn > first page'),
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };

  const optionsInit = {
    debugInvalidOptions: true,
    pagination: true,
    movableRows: false,
    resizableRows: false,
    paginationSize: 5,
    rowHeight: 41,
    locale: 'ko',
    index: 'group_code',
    langs: {
      ko: languageTabulator(),
    },
    selectableRows: 1,
    selectableRowsCheck: (row: Tabulator.RowComponent) => {
      return !row.getElement().classList.contains('tabulator-selected');
    },
    footerElement: `<div id="footer-bottom" style="padding: 0 20px 0 0; text-align: right;">${totalText} ${
      groupsData?.length || 0
    } ${resultText}</div>`,
    placeholder: `<div style='text-align:center;'>${t('msg > no data')}</div>`,
  };

  const optionsAssign = {
    height: '270px',
    placeholder: `<div style='text-align:center;'>${t('msg > no data')}</div>`,
    debugInvalidOptions: true,
    movableRows: false,
    resizableRows: false,
    layout: 'fitColumns',
    rowHeight: 41,
    selectableRows: true,
    footerElement: `<div id="footer-bottom" style="padding: 0 20px 0 0; text-align: right;">${totalText} ${
      usersAssign?.length || 0
    } ${resultText}</div>`,
  };

  const optionsStandby = {
    placeholder: `<div style='text-align:center;'>${t('msg > no data')}</div>`,
    debugInvalidOptions: true,
    movableRows: false,
    resizableRows: false,
    height: '270px',
    layout: 'fitColumns',
    rowHeight: 41,
    selectableRows: true,
    footerElement: `<div id="footer-bottom" style="padding: 0 20px 0 0; text-align: right;">${totalText} ${
      users?.length || 0
    } ${resultText}</div>`,
  };

  useEffect(() => {
    if (userGroupData) {
      setUsersAssign(userGroupData);
      setInitialUsersAssign(userGroupData);
    }
  }, [userGroupData]);

  useEffect(() => {
    if (usersData) {
      setUsers(usersData);
    }
  }, [usersData]);

  useEffect(() => {
    if (Array.isArray(usersData) && Array.isArray(usersAssign)) {
      const filteredUsers = usersData.filter(
        (user) => !usersAssign.some((assigned) => assigned.id === user.id)
      );

      setUsers((prev) => {
        const isSame =
          prev.length === filteredUsers.length &&
          prev.every((u, i) => u.id === filteredUsers[i]?.id);

        return isSame ? prev : filteredUsers;
      });
    }
  }, [usersAssign, usersData]);

  const optionsRadioFilterUsage = commonListData?.['002']
    ? [
        { value: '', label: t('cmn > all'), code: 'All' },
        ...(commonListData['002']?.code?.map((code, index) => ({
          value: code ?? '',
          label: commonListData['002']?.name[index] ?? '',
          code: code ?? '',
        })) || []),
      ]
    : [];

  const optionsRadioFilterPosition = commonListData?.['021']
    ? [
        { value: '', label: t('cmn > all'), code: 'All' },
        ...(commonListData['021']?.code?.map((code, index) => ({
          value: code ?? '',
          label: commonListData['021']?.name[index] ?? '',
          code: code ?? '',
        })) || []),
      ]
    : [];

  const accountTypeOptions = [
    { value: 'All', label: t('cmn > all') },
    { value: 'internal', label: t('user > account type general') },
    { value: 'healthcare', label: t('user > account type hospital') },
  ];

  const handleOnChangeInputSelect = useCallback(({ target }: { target: { value: string } }) => {
    const { value } = target;
    setSelectedIsCodeGroup(value);
  }, []);

  const handleRestore = () => {
    setUsersAssign(initialUsersAssign);
    setHasChanged(false);
    setSelectedRowAssign(selectedRowStandby);
    setSelectedRowStandby(selectedRowAssign);
    if (isNewClicked) {
      setUsers(usersData);
      setSelectedRowStandby([]);
    }
  };

  const handleSearchInit = useCallback(
    (inputVal: string | null = null) => {
      const params: GroupParams = {};

      if (inputVal) {
        params.input = inputVal;
      }

      if (selectedIsCodeGroup) {
        params.is_used = selectedIsCodeGroup;
      }

      setQueryParams(params);
    },
    [selectedIsCodeGroup]
  );

  const handleResetInit = () => {
    setSelectedIsCodeGroup('');
  };

  const handleRowInitSelected = (row: Tabulator.RowComponent) => {
    const rowData = row.getData();
    const groupCode = rowData.group_code;
    const groupName = rowData.group_name;

    const updateGroupData = () => {
      setDisabled(false);
      enableUPDATEButtons();
      setGroupName(groupName);
      setSelectedCode({ group_code: groupCode });
      setIsRowClicked(true);
      setButtonState((prevState) => ({
        ...prevState,
        arrow: false,
        confirm: true,
        restore: false,
        create: false,
      }));
      setIsNewClicked(false);
      setDisabledLog(false);
      setSelectedRowAssign([]);
      setSelectedRowStandby([]);
    };

    if (hasChangedRef.current) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > delete confirm'), {
        mode: 'confirm',
      });

      message.confirmClicked().then(() => {
        updateGroupData();
        setHasChanged(false);
      });
    } else {
      updateGroupData();
    }
  };

  const handleRowSelectedAssign = (row: User[]) => {
    setSelectedRowAssign(row);
  };

  const handleRowSelectedStandBy = (row: User[]) => {
    if (rowClickedRef.current || newClickedRef.current) {
      setSelectedRowStandby(row);
    } else {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > group not selected'));
      tbRefStandby.current?.deselectRow();
      return;
    }
  };

  const handleLeftButton = () => {
    if (selectedRowAssign && selectedRowAssign.length > 0) {
      setHasChanged(true);
      if (isNewClicked) {
        setButtonState((prevState) => ({
          ...prevState,
          confirm: true,
          restore: false,
          arrow: false,
          create: false,
        }));
      } else {
        setButtonState((prevState) => ({
          ...prevState,
          confirm: false,
          restore: false,
          arrow: false,
          create: true,
        }));
      }

      const selectedIds = selectedRowAssign.map((user) => user.id);

      setUsersAssign((prev) => {
        const updatedAssign = prev.filter((user) => !selectedIds.includes(user.id));
        return updatedAssign;
      });

      setUsers((prev) => {
        const existingIds = new Set(prev?.map((user) => user.id));
        const newUsers = (
          Array.isArray(selectedRowAssign) ? selectedRowAssign : [selectedRowAssign]
        ).filter((user) => !existingIds.has(user.id));

        const updatedUsers = [...prev, ...newUsers];
        return updatedUsers;
      });
      setSelectedRowStandby((prev) => {
        const prevStandby = Array.isArray(prev) ? prev : [];
        return [...prevStandby, ...selectedRowAssign];
      });

      setSelectedRowAssign([]);
    } else {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > group not selected'));
    }
  };

  const handleRightButton = () => {
    if (selectedRowStandby && selectedRowStandby.length > 0) {
      setHasChanged(true);
      if (isNewClicked) {
        setButtonState((prevState) => ({
          ...prevState,
          confirm: true,
          restore: false,
          arrow: false,
          create: false,
        }));
      } else {
        setButtonState((prevState) => ({
          ...prevState,
          confirm: false,
          restore: false,
          arrow: false,
          create: true,
        }));
      }

      const selectedIds = selectedRowStandby.map((user) => user.id);

      setUsers((prev) => {
        const updatedUsers = prev.filter((user) => !selectedIds.includes(user.id));
        return updatedUsers;
      });

      setUsersAssign((prev = []) => {
        const existingIds = new Set(prev.map((user) => user.id));
        const newUsersAssign = (
          Array.isArray(selectedRowStandby) ? selectedRowStandby : [selectedRowStandby]
        ).filter((user) => !existingIds.has(user.id));

        const updatedAssign = [...prev, ...newUsersAssign];

        return updatedAssign;
      });

      setSelectedRowAssign((prev) => {
        const prevAssign = Array.isArray(prev) ? prev : [];
        return [...prevAssign, ...selectedRowStandby];
      });

      setSelectedRowStandby([]);
    } else {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > group not selected'));
    }
  };

  const handleDblStandbyClick = (_e: UIEvent, row: Tabulator.RowComponent) => {
    if (row) {
      setHasChanged(true);
      const rowData = row.getData();
      if (selectedCode?.group_code || newClickedRef.current) {
        setUsers((prevUsers) => prevUsers.filter((user) => user.id !== rowData.id));
        setUsersAssign((prevUsersAssign) => {
          return [...(prevUsersAssign || []), rowData];
        });
        setSelectedRowAssign([rowData]);
        if (newClickedRef.current) {
          setButtonState((prevState) => ({
            ...prevState,
            confirm: true,
            restore: false,
            arrow: false,
            create: false,
          }));
        } else if (selectedCode.group_code) {
          setButtonState((prevState) => ({
            ...prevState,
            confirm: false,
            restore: false,
            arrow: false,
            create: false,
          }));
        } else {
          setButtonState((prevState) => ({
            ...prevState,
            confirm: true,
            restore: true,
            arrow: true,
            create: false,
          }));
        }
      } else {
        setHasChanged(false);
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > group not selected'));
      }
    }
  };

  const handleDblAssignClick = (_e: UIEvent, row: Tabulator.RowComponent) => {
    if (row) {
      setHasChanged(true);
      const rowData = row.getData();
      setUsersAssign((prevUsersAssign) => prevUsersAssign.filter((user) => user.id !== rowData.id));

      setUsers((prevUsers) => {
        return [...(prevUsers || []), rowData];
      });
      setSelectedRowStandby([rowData]);
      if (isNewClicked) {
        setButtonState((prevState) => ({
          ...prevState,
          confirm: true,
          restore: false,
          arrow: false,
          create: false,
        }));
      } else {
        setButtonState((prevState) => ({
          ...prevState,
          confirm: false,
          restore: false,
          arrow: false,
          create: false,
        }));
      }
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));

    if (isNewClicked) {
      setHasChangesCreate(true);
    } else {
      setHasChangesUpdate(true);
    }
  };

  const handleOnChangeInputSelectPosition = useCallback(
    ({ target }: { target: { value: string } }) => {
      const { value } = target;
      setSelectedPosition(value);
    },
    []
  );

  const handleSearchStandby = useCallback(
    (inputVal: string | null = null) => {
      const params: UserParams = {};

      if (inputVal) {
        params.input = inputVal;
      }
      if (selectedPosition) {
        params.position = selectedPosition;
      }

      setQueryParamsUser(params);
    },
    [selectedPosition]
  );

  const handleResetStandby = () => {
    setSelectedPosition('');
    setSelectedAccountType('All');
  };

  const handleCancelButtonClick = () => {
    if (selectedCode.group_code) {
      if (hasChangesUpdate || hasChanged) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message.confirmClicked().then(() => {
          reloadCallback();
        });
      } else {
        reloadCallback();
      }
    } else if (isNewClicked) {
      if (hasChangesCreate || hasChanged) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message.confirmClicked().then(() => {
          emptyDetail();
          setIsNewClicked(false);
          setDisabled(true);
          enableInitialButtons();
          setHasChangesCreate(false);
          setHasChanged(false);
          setSelectedRowAssign([]);
          setSelectedRowStandby([]);
          setUsers(usersData);
          setUsersAssign([]);
          setIsRowClicked(false);
          tbRefAssign.current?.deselectRow();
          tbRefStandby.current?.deselectRow();
        });
      } else {
        emptyDetail();
        setHasChanged(false);
        setIsNewClicked(false);
        setDisabled(true);
        enableInitialButtons();
        setHasChangesCreate(false);
        setSelectedCode({ group_code: null });
        setIsRowClicked(false);
        tbRefStandby.current?.deselectRow();
      }
    } else {
      reloadCallback();
      setHasChanged(false);
    }
  };

  const handleNewButtonClick = () => {
    if (selectedCode?.group_code) {
      if (hasChanged || hasChangesUpdate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message.confirmClicked().then(() => {
          tbRefInit.current?.deselectRow();
          emptyDetail();
          setHasChangesUpdate(false);
          enableRegisterButtons();
          setIsNewClicked(true);
          setSelectedCode({ group_code: null });
          setIsRowClicked(false);
          setUsersAssign(initialUsersAssign);
          setUsers(usersData);
        });
      } else {
        tbRefInit.current?.deselectRow();
        emptyDetail();
        setHasChangesUpdate(false);
        enableRegisterButtons();
        setIsNewClicked(true);
        setSelectedCode({ group_code: null });
        setIsRowClicked(false);
        setUsersAssign(initialUsersAssign);
        setUsers(usersData);
      }
    } else {
      tbRefInit.current?.deselectRow();
      emptyDetail();
      setDisabled(false);
      setIsNewClicked(true);
      setIsRowClicked(false);
      enableRegisterButtons();
      setHasChangesUpdate(false);
    }
  };

  const handleRegistButtonClick = () => {
    createGroupMutation.mutateAsync(formValues);
  };

  const handleConfirmButtonClick = () => {
    const assignDataAssign = {
      group_name: formValues.group_name,
      description: formValues.description,
      usage: formValues.usage,
      user_id: Array.isArray(usersAssign) ? usersAssign.map((item) => item.id) : [],
    };
    const assignDataInfo = formValues;

    if (selectedCode?.group_code != null) {
      updateUserGroupMutation.mutateAsync({
        id: selectedCode?.group_code,
        group: assignDataInfo,
        users: assignDataAssign,
      });
    }
  };

  const handleDeleteButtonClick = () => {
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('msg > delete confirm'), {
      mode: 'confirm',
    });
    message.confirmClicked().then(() => {
      if (selectedCode?.group_code != null) {
        deleteUserMutation.mutateAsync(selectedCode?.group_code);
      }
    });
  };

  useEffect(() => {
    if (groupData) {
      // API returns array at runtime despite single-object type declaration
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const firstGroup = (groupData as any)[0];
      if (firstGroup) {
        setFormValues((prevValues) => ({
          ...prevValues,
          group_name: firstGroup.group_name ?? '',
          usage: firstGroup.is_used ?? '',
          description: firstGroup.description ?? '',
        }));
      }
    }
  }, [groupData]);

  const logs =
    groupData && Array.isArray(groupData)
      ? [
          {
            label: t('cmn > registered by'),
            value: groupData[0]?.registered_by ?? '',
          },
          {
            label: t('cmn > registered time'),
            value: groupData[0]?.registered_time
              ? formatFullDateTimeDDMMYYYY(new Date(groupData[0].registered_time))
              : '',
          },
          {
            label: t('cmn > updated by'),
            value: groupData[0]?.updated_by ?? '',
          },
          {
            label: t('cmn > updated time'),
            value: groupData[0]?.updated_time
              ? formatFullDateTimeDDMMYYYY(new Date(groupData[0].updated_time))
              : '',
          },
        ]
      : [];

  return (
    <>
      <section className="wrap">
        <div className="header-title">
          <h3>{t('SYSTEM')}</h3>
          <h3>&gt;</h3>
          <h3>{t('SYSTEM-GROUP')}</h3>
        </div>

        <ContainerCard>
          <Filtering
            searchRef={searchRef}
            placeholder={`${t('authority > name')} / ${t('code > description')}`}
            onSearch={handleSearchInit}
            onReset={handleResetInit}
            labelSelect={t('002')}
            disableFiltering={true}
          >
            <Select
              value={selectedIsCodeGroup}
              onChange={handleOnChangeInputSelect}
              options={optionsRadioFilterUsage}
              className={'custom-select'}
            />
          </Filtering>
        </ContainerCard>
      </section>
      <ContainerCard>
        <ReactTabulator
          data={groupsData || []}
          columns={columnsHistory as unknown as ColumnDefinition[]}
          layout={'fitDataStretch'}
          options={optionsInit}
          onRef={(r) => {
            tbRefInit.current = r.current;
          }}
          events={{
            rowSelected: handleRowInitSelected,
            tableBuilt: () => {
              if (selectedCode?.group_code) {
                const row = tbRefInit.current?.getRow(selectedCode?.group_code) || null;
                row?.select();
              } else if (newId) {
                const row = tbRefInit.current?.getRow(newId) || null;
                if (row) {
                  tbRefInit.current?.setPageToRow(row);
                  tbRefInit.current?.scrollToRow(row, 'bottom', true);
                  tbRefInit.current?.selectRow([newId]);
                }
              }
            },
          }}
        />
      </ContainerCard>
      <div className="flex gap-4 items-center ">
        <div className="flex-1 min-w-0">
          <ContainerCard>
            <div className="mb-5">
              <div className="flex gap-4 justify-between">
                <div className="w-32 flex items-center gap-2">
                  <span className="text-body-2 text-au-neutral-4 py-[9px] flex-grow whitespace-nowrap">
                    {groupName || ''}
                  </span>
                </div>
                <div className="flex gap-4 ml-auto">
                  <Button
                    label={t('cmn > reset table')}
                    customButton="btn-search"
                    disabled={buttonState.restore}
                    onClick={() => handleRestore?.()}
                  />
                </div>
              </div>
            </div>
            <ReactTabulator
              key={usersAssign?.length}
              className="container-tabullator"
              data={usersAssign || []}
              columns={columnAssign as unknown as ColumnDefinition[]}
              layout={'fitDataStretch'}
              options={optionsAssign}
              onRef={(r) => {
                tbRefAssign.current = r.current;
              }}
              events={{
                rowSelectionChanged: handleRowSelectedAssign,
                rowDblClick: handleDblAssignClick,
                tableBuilt: () => {
                  if (selectedRowAssign) {
                    tbRefAssign.current?.selectRow(selectedRowAssign.map((p) => p.id));
                  }
                },
              }}
            />
          </ContainerCard>
        </div>
        <div className="flex-shrink-0 min-w-0">
          <div className="flex flex-col items-center space-y-4">
            <button
              type="button"
              className="_slideLeft"
              id="exclusion"
              disabled={buttonState.arrow}
              onClick={handleLeftButton}
            >
              <img src={arrowLeft} alt="Arrow Left" />
            </button>
            <button
              type="button"
              className="_slideRight"
              id="allocation"
              disabled={buttonState.arrow}
              onClick={handleRightButton}
            >
              <img src={arrowRight} alt="Arrow Right" />
            </button>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <ContainerCard>
            <div className="mb-5">
              <Filtering
                placeholder={t('authority > name')}
                onSearch={handleSearchStandby}
                onReset={handleResetStandby}
                disableFiltering={true}
                showLabel={false}
                customWidthInput={'flex-1 basis-[200px]'}
                customWidthSelect={'flex-1 basis-[420px]'}
              >
                <div className="flex flex-1 gap-3">
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="text-sm text-au-neutral-4">{t('user > account type')}</span>
                    <Select
                      options={accountTypeOptions}
                      className={'custom-select'}
                      value={selectedAccountType}
                      onChange={({ target }) =>
                        setSelectedAccountType(
                          target.value === 'internal' || target.value === 'healthcare'
                            ? target.value
                            : 'All'
                        )
                      }
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="text-sm text-au-neutral-4">{t('021')}</span>
                    <Select
                      options={optionsRadioFilterPosition}
                      className={'custom-select'}
                      value={selectedPosition}
                      onChange={handleOnChangeInputSelectPosition}
                    />
                  </div>
                </div>
              </Filtering>
            </div>
            <ReactTabulator
              key={users?.length}
              className="container-tabullator"
              data={displayedUsers || []}
              columns={columnStandby as unknown as ColumnDefinition[]}
              layout={'fitDataStretch'}
              options={optionsStandby}
              onRef={(r) => {
                tbRefStandby.current = r.current;
              }}
              events={{
                rowSelectionChanged: handleRowSelectedStandBy,
                rowDblClick: handleDblStandbyClick,
                tableBuilt: () => {
                  if (selectedRowStandby) {
                    tbRefStandby.current?.selectRow(selectedRowStandby.map((p) => p.id));
                  }
                },
              }}
            />
          </ContainerCard>
        </div>
      </div>
      <ContainerCard>
        <div className="flex w-full flex-col gap-[10px] ">
          <div className="grid grid-cols-3 gap-x-[40px] gap-y-[10px] h-[85px] items-start">
            <DetailForm
              formRef={groupRef}
              label={t('user > group name')}
              value={formValues.group_name}
              onChange={handleInputChange}
              name={'group_name'}
              inputType={'text'}
              required={true}
              disabled={disabled}
            />
            <DetailForm
              label={t('code > usage')}
              name="usage"
              value={formValues.usage}
              inputType={'select'}
              onChange={handleInputChange}
              required={true}
              disabled={disabled}
              optionSelect={
                commonListData?.['002']
                  ? [
                      ...(commonListData['002']?.code?.map((code, index) => ({
                        value: code ?? '',
                        label: commonListData['002']?.name[index] ?? '',
                      })) || []),
                    ]
                  : []
              }
            />
            <DetailForm
              label={t('code > description')}
              value={formValues.description}
              onChange={handleInputChange}
              name={'description'}
              inputType={'textarea'}
              disabled={disabled}
              className={'flex-grow  '}
            />
          </div>
          <hr className="border-t border-gray-300 mt-3" />
          <div className="flex items-center justify-between gap-4 w-full">
            <div className="flex-1">{disabledLog ? null : <LogList logs={logs} />}</div>

            <div className="flex-none">
              <ButtonGroup
                onClickDelete={handleDeleteButtonClick}
                onClickNew={handleNewButtonClick}
                onClickCancel={handleCancelButtonClick}
                onClickRegist={handleRegistButtonClick}
                onClickConfirm={handleConfirmButtonClick}
                isNewClicked={isNewClicked}
                cancelButtonState={buttonState.cancel}
                confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
                deleteButtonState={buttonState.delete}
                newButtonState={hasChangesCreate ? false : buttonState.create}
              />
            </div>
          </div>
        </div>
      </ContainerCard>
    </>
  );
};

export default GroupManagement;
